import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User, CampaignLimits } from 'api/types/user';
import { FeatureFlags } from 'api/types/featureFlags';

export type ImpersonatedUserInfo = Pick<User, 'id' | 'email' | 'firstName' | 'lastName'>;

const IMPERSONATION_STORAGE_KEY = 'dmsark_impersonate';

export function getStoredImpersonation(): Pick<
  AuthState,
  'impersonatedUserId' | 'impersonatedUser'
> {
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
    if (!raw) return { impersonatedUserId: null, impersonatedUser: null };
    const parsed = JSON.parse(raw) as {
      impersonatedUserId?: string | null;
      impersonatedUser?: ImpersonatedUserInfo | null;
    };
    return {
      impersonatedUserId: parsed.impersonatedUserId ?? null,
      impersonatedUser: parsed.impersonatedUser ?? null
    };
  } catch {
    return { impersonatedUserId: null, impersonatedUser: null };
  }
}

export function syncImpersonationToStorage(
  impersonatedUserId: string | null,
  impersonatedUser: ImpersonatedUserInfo | null
): void {
  try {
    if (impersonatedUserId && impersonatedUser) {
      sessionStorage.setItem(
        IMPERSONATION_STORAGE_KEY,
        JSON.stringify({ impersonatedUserId, impersonatedUser })
      );
    } else {
      sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
    }
  } catch {
    // ignore
  }
}

export function clearImpersonationStorage(): void {
  try {
    sessionStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  } catch {
    // ignore
  }
}

interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  featureFlags?: FeatureFlags;
  campaignLimits?: CampaignLimits | null;
  intercomJwt?: string | null;
  impersonatedUserId: string | null;
  impersonatedUser: ImpersonatedUserInfo | null;
}

const stored = getStoredImpersonation();

const initialState: AuthState = {
  isAuthenticated: false,
  isLoading: true,
  user: null,
  featureFlags: undefined,
  campaignLimits: null,
  intercomJwt: null,
  impersonatedUserId: stored.impersonatedUserId,
  impersonatedUser: stored.impersonatedUser
};

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthState: (state, action: PayloadAction<Partial<AuthState>>) => {
      if (action.payload.isAuthenticated !== undefined)
        state.isAuthenticated = action.payload.isAuthenticated;
      if (action.payload.isLoading !== undefined) state.isLoading = action.payload.isLoading;
      if (action.payload.user !== undefined) {
        const previousUser = state.user;
        const userIdChanged = action.payload.user?.id !== previousUser?.id;
        state.user = action.payload.user;
        if (userIdChanged) {
          state.featureFlags = undefined;
          state.campaignLimits = null;
          // Only clear impersonation when switching from one real user to another,
          // not when hydrating from null (initial load) to preserve sessionStorage state
          if (previousUser !== null) {
            state.impersonatedUserId = null;
            state.impersonatedUser = null;
          }
        }
      }
      if (action.payload.featureFlags !== undefined)
        state.featureFlags = action.payload.featureFlags;
      if (action.payload.campaignLimits !== undefined)
        state.campaignLimits = action.payload.campaignLimits;
      if (action.payload.intercomJwt !== undefined)
        state.intercomJwt = action.payload.intercomJwt ?? null;
      if (action.payload.impersonatedUserId !== undefined)
        state.impersonatedUserId = action.payload.impersonatedUserId ?? null;
      if (action.payload.impersonatedUser !== undefined)
        state.impersonatedUser = action.payload.impersonatedUser ?? null;
    },
    setImpersonatedUser: (state, action: PayloadAction<ImpersonatedUserInfo | null>) => {
      if (action.payload) {
        state.impersonatedUserId = action.payload.id;
        state.impersonatedUser = action.payload;
      } else {
        state.impersonatedUserId = null;
        state.impersonatedUser = null;
      }
    },
    clearImpersonation: (state) => {
      state.impersonatedUserId = null;
      state.impersonatedUser = null;
    }
  }
});

export const { setAuthState, setImpersonatedUser, clearImpersonation } = authSlice.actions;
export default authSlice.reducer;
